import { format, isValid, parse } from 'date-fns';

export const minRequestedDateMin = () => {
    const today = new Date();
    let year = today.getFullYear();
    let month = today.getMonth();
    month -= 3;

    // 月が負の場合は前年に補正
    if (month < 0) {
        month += 12;
        year -= 1;
    }
    return new Date(year, month, today.getDate());
};

export const minStartDateMin = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - 14);

    return date;
};

export const maxStartDateMin = () => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 14);

    return date;
};

export const maxEndDateMin = (timestamp) => {
    const date = timestamp ? new Date(timestamp) : new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 30);

    return date;
};

/**
 * 元タイムスタンプを基準に、
 * 二週間後のタイムスタンプを返す
 *
 * @param {number} timestamp 元タイムスタンプ
 * @returns {number} 二週間後のタイムスタンプ
 */
export const getDateTwoWeeksLater = (timestamp) => {
    let maxEndDateMin = timestamp ? new Date(timestamp) : new Date();
    return maxEndDateMin.setDate(maxEndDateMin.getDate() + 14);
};

/**
 * 元タイムスタンプを基準に、
 * 2ヶ月前のタイムスタンプを返す
 * （存在しない場合翌月1日に繰り上げ）
 *
 * @param {number} timestamp 元タイムスタンプ
 * @returns {number} 2ヶ月前のタイムスタンプ
 */
export const getDateTwoMonthsAgo = (timestamp) => {
    const baseDate = timestamp ? new Date(timestamp) : new Date();

    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const date = baseDate.getDate();

    // 2ヶ月前の月
    const target = new Date(year, month - 2, 1);

    const targetYear = target.getFullYear();
    const targetMonth = target.getMonth();

    // 月末取得
    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();

    // 存在する場合
    if (date <= lastDay) {
        return new Date(targetYear, targetMonth, date).getTime();
    }

    // 存在しない場合 → 翌月1日固定
    return new Date(targetYear, targetMonth + 1, 1).getTime();
};

/**
 * 基準日の翌月同日を返す
 *
 * @param {string | number | Date} date 基準日
 * @returns {Date} 翌月同日（翌月に同日が存在しない場合は翌月末）
 */
export const getSameDayOfNextMonth = (date) => {
    const baseDate = new Date(date);

    const year = baseDate.getFullYear();
    const month = baseDate.getMonth();
    const day = baseDate.getDate();

    const lastDayOfNextMonth = new Date(year, month + 2, 0).getDate();

    return new Date(year, month + 1, Math.min(day, lastDayOfNextMonth));
};

export const formattedDate = (timestamp) => {
    let date = new Date(timestamp);
    return date.getFullYear() + '/' + ('0' + (date.getMonth() + 1)).slice(-2) + '/' + ('0' + date.getDate()).slice(-2);
};

/**
 * 日付を yyyy/MM/dd に変換（未完成入力はNG）
 * - 日付型は許可
 * - 文字列は「yyyy/m/d」のみ許可
 * - それ以外はnullでreturn
 *
 * @param {string | Date} input
 * @returns {string | null}
 */
export const formatToYYYYMMDD = (input) => {
    // Date型の場合（未完成入力の場合null）
    if (input instanceof Date) {
        return isValid(input) ? format(input, 'yyyy/MM/dd') : null;
    }

    // 文字列以外はnull
    if (typeof input !== 'string') return null;
    // 「yyyy/m/d」以外はnull
    if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(input)) return null;

    // Date型に変換し存在しない日付の場合null
    const date = parse(input, 'yyyy/M/d', new Date());
    if (!isValid(date)) return null;

    return format(date, 'yyyy/MM/dd');
};

/**
 * 日付が本日より前かチェック
 *
 * @param {string | null} date 文字列の「yyyy/MM/dd」
 * @returns {boolean} dateが本日より前の場合のみtrue（未入力の場合はfalse）
 */
export function isPastDate(date) {
    if (!date) return false;
    return date < formatToYYYYMMDD(new Date());
}

/**
 * 開始日が終了日よりも大きいかチェック
 *
 * @param {string | null} startDate 文字列の「yyyy/MM/dd」
 * @param {string | null} endDate 文字列の「yyyy/MM/dd」
 * @returns {boolean} startDateがendDateよりも大きい場合のみtrue
 */
export function isStartAfterEnd(startDate, endDate) {
    if (startDate && endDate) {
        if (startDate > endDate) {
            return true;
        }
    }
    return false;
}

/**
 * 週末特売の対象曜日（金・土・日）かどうかを判定する
 *
 * @param {string | Date} date 判定する日付
 * @returns {boolean} 金・土・日の場合のみ true
 */
export const isWeekendSaleDay = (date) => {
    return [5, 6, 0].includes(new Date(date).getDay());
};

/**
 * 基準日から指定日数ずらした日付を返す
 *
 * @param {string | number | Date} baseDate 基準日
 * @param {number} diffDays ずらす日数（負の値で過去日）
 * @returns {Date} 基準日から diffDays 日後の日付（時刻は 00:00:00）
 */
export const getRelativeDate = (baseDate, diffDays) => {
    const date = new Date(baseDate);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + diffDays);
    return date;
};

/**
 * 日付が範囲内かどうかを判定する
 *
 * @param {Date} date 判定する日付
 * @param {Date} min 範囲の下限
 * @param {Date} max 範囲の上限
 * @returns {boolean} min 以上 max 以下の場合のみ true（下限・上限を含む。不正な日付は false）
 */
export const isInDateRange = (date, min, max) => {
    return min <= date && date <= max;
};
